import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Customers from "@/pages/admin/Customers";
import ResourceCrud from "@/pages/admin/ResourceCrud";
import FormsManager from "@/pages/admin/FormsManager";
import Overview from "@/components/dashboard/Overview";
import RequestsTable from "@/components/dashboard/RequestsTable";
import { LaborCalc, MarginsCalc } from "@/pages/admin/Tools";
import { Dispatch, CalendarView } from "@/pages/admin/DispatchCalendar";
import DispatchBoard from "@/components/dashboard/DispatchBoard";
import CalendarBoard from "@/components/dashboard/CalendarBoard";
import MembershipManager from "@/components/dashboard/MembershipManager";
import PartsCatalog from "@/components/dashboard/PartsCatalog";
import FieldService from "@/components/dashboard/FieldService";
import LaunchSelect from "@/components/dashboard/LaunchSelect";
import SellingComfort from "@/components/dashboard/SellingComfort";
import WarrantyCompliance from "@/components/dashboard/WarrantyCompliance";
import GuaranteesGrid from "@/components/GuaranteesGrid";
import { createForm } from "@/lib/dashboardApi";
import WeatherIntel from "@/pages/admin/WeatherIntel";
import Pricebook from "@/pages/admin/Pricebook";
import TechJobs from "@/pages/admin/TechJobs";
import EquipmentHub from "@/pages/admin/EquipmentHub";
import LoadCalc from "@/pages/admin/LoadCalc";
import LeadCenter from "@/components/dashboard/LeadCenter";
import ProposalFollowup from "@/components/dashboard/ProposalFollowup";
import ProspectingEngine from "@/components/dashboard/ProspectingEngine";
import InfoPanel from "@/pages/admin/InfoPanel";
import RoleDashboard from "@/components/dashboard/RoleDashboard";
import BlogEngine from "@/components/dashboard/BlogEngine";
import SeoCommand from "@/components/dashboard/SeoCommand";
import DraftReview from "@/components/dashboard/DraftReview";
import ConnectReadyDashboard from "@/components/dashboard/ConnectReadyDashboard";
import KeywordIntelligence from "@/components/dashboard/KeywordIntelligence";
import TopicOwnership from "@/components/dashboard/TopicOwnership";
import LocalGravity from "@/components/dashboard/LocalGravity";
import CompetitorTopics from "@/components/dashboard/CompetitorTopics";
import GiveawayAdmin from "@/components/dashboard/GiveawayAdmin";
import { ROLES, DEFAULT_ROLE, roleFor, canSee } from "@/lib/roles";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Rocket, LogOut, Inbox, Building2, Wrench, MapPin, FileText, Star, Tag,
  Briefcase, GitBranch, ShieldCheck, Loader2, Plus, Pencil, Trash2, ExternalLink, Menu, Sparkles,
  LayoutDashboard, TrendingUp, ArrowRight, Users, Receipt, Package, Wallet, Truck,
  Send, Calendar, CalendarDays, Crown, ClipboardList, CloudSun, Search, DollarSign,
  Calculator, Smartphone, Megaphone, Gauge, Contact, LineChart, Swords, Bot, KeyRound, Layers, Award, BookOpen, Timer,
} from "lucide-react";

const RESOURCE_CONFIGS = {
  invoices: {
    endpoint: "/invoices", title: "Invoices", required: "customer",
    searchKeys: ["number", "customer", "status"],
    columns: [
      { key: "number", label: "Invoice #" },
      { key: "customer", label: "Customer" },
      { key: "amount", label: "Amount", render: (r) => (r.amount ? `$${r.amount}` : "") },
      { key: "status", label: "Status" },
      { key: "due_date", label: "Due" },
    ],
    fields: [
      { key: "customer", label: "Customer" },
      { key: "number", label: "Invoice Number" },
      { key: "amount", label: "Amount ($)" },
      { key: "status", label: "Status", type: "select", options: ["Draft", "Sent", "Paid", "Overdue"] },
      { key: "due_date", label: "Due Date (YYYY-MM-DD)" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  parts: {
    endpoint: "/parts", title: "Parts", required: "name",
    searchKeys: ["name", "sku"],
    columns: [
      { key: "name", label: "Part" },
      { key: "sku", label: "SKU" },
      { key: "quantity", label: "Qty" },
      { key: "cost", label: "Cost", render: (r) => (r.cost ? `$${r.cost}` : "") },
    ],
    fields: [
      { key: "name", label: "Part Name" },
      { key: "sku", label: "SKU / Part #" },
      { key: "quantity", label: "Quantity in Stock" },
      { key: "cost", label: "Unit Cost ($)" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  expenses: {
    endpoint: "/expenses", title: "Expenses", required: "description",
    searchKeys: ["description", "category", "vendor"],
    columns: [
      { key: "description", label: "Description" },
      { key: "category", label: "Category" },
      { key: "amount", label: "Amount", render: (r) => (r.amount ? `$${r.amount}` : "") },
      { key: "vendor", label: "Vendor" },
      { key: "date", label: "Date" },
    ],
    fields: [
      { key: "description", label: "Description" },
      { key: "category", label: "Category", type: "select", options: ["Fuel", "Tools", "Parts", "Marketing", "Payroll", "Office", "Other"] },
      { key: "amount", label: "Amount ($)" },
      { key: "vendor", label: "Vendor" },
      { key: "date", label: "Date (YYYY-MM-DD)" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  vendors: {
    endpoint: "/vendors", title: "Vendors", required: "name",
    searchKeys: ["name", "phone", "email", "category"],
    columns: [
      { key: "name", label: "Vendor" },
      { key: "category", label: "Category" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
    ],
    fields: [
      { key: "name", label: "Vendor Name" },
      { key: "category", label: "Category" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "address", label: "Address" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  "field-service": {
    endpoint: "/servicejobs", title: "Field Service", required: "customer",
    searchKeys: ["customer", "service", "tech", "status"],
    columns: [
      { key: "customer", label: "Customer" },
      { key: "service", label: "Service" },
      { key: "tech", label: "Technician" },
      { key: "status", label: "Status" },
      { key: "scheduled_date", label: "Date" },
    ],
    fields: [
      { key: "customer", label: "Customer" },
      { key: "service", label: "Service / Job" },
      { key: "tech", label: "Assigned Technician" },
      { key: "status", label: "Status", type: "select", options: ["Unassigned", "Scheduled", "In Progress", "Completed"] },
      { key: "scheduled_date", label: "Scheduled Date (YYYY-MM-DD)" },
      { key: "address", label: "Address" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  membership: {
    endpoint: "/plans", title: "Membership Plans", required: "name",
    searchKeys: ["name", "billing"],
    columns: [
      { key: "name", label: "Plan" },
      { key: "price", label: "Price", render: (r) => (r.price ? `$${r.price}` : "") },
      { key: "billing", label: "Billing" },
    ],
    fields: [
      { key: "name", label: "Plan Name" },
      { key: "price", label: "Price ($)" },
      { key: "billing", label: "Billing", type: "select", options: ["Monthly", "Annual", "One-time"] },
      { key: "benefits", label: "Benefits", type: "textarea" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  "comfort-club": {
    endpoint: "/members", title: "Comfort Club Members", required: "name",
    searchKeys: ["name", "plan", "phone", "email"],
    columns: [
      { key: "name", label: "Member" },
      { key: "plan", label: "Plan" },
      { key: "phone", label: "Phone" },
      { key: "renewal_date", label: "Renewal" },
      { key: "status", label: "Status" },
    ],
    fields: [
      { key: "name", label: "Member Name" },
      { key: "plan", label: "Plan" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "address", label: "Address" },
      { key: "renewal_date", label: "Renewal Date (YYYY-MM-DD)" },
      { key: "status", label: "Status", type: "select", options: ["Active", "Lapsed", "Cancelled"] },
    ],
  },
  "content-planner": {
    endpoint: "/content_plan", title: "Content Planner", required: "title",
    searchKeys: ["title", "type", "status"],
    columns: [
      { key: "title", label: "Title" },
      { key: "type", label: "Type" },
      { key: "status", label: "Status" },
      { key: "publish_date", label: "Publish" },
    ],
    fields: [
      { key: "title", label: "Content Title" },
      { key: "type", label: "Type", type: "select", options: ["Blog", "Social", "Email", "Video", "Landing Page"] },
      { key: "status", label: "Status", type: "select", options: ["Idea", "Draft", "Scheduled", "Published"] },
      { key: "publish_date", label: "Publish Date (YYYY-MM-DD)" },
      { key: "notes", label: "Notes / Brief", type: "textarea" },
    ],
  },
  team: {
    endpoint: "/team", title: "Team", required: "name",
    searchKeys: ["name", "role", "phone", "email"],
    columns: [
      { key: "name", label: "Name" },
      { key: "role", label: "Role" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "status", label: "Status" },
    ],
    fields: [
      { key: "name", label: "Full Name" },
      { key: "role", label: "Role", type: "select", options: ["Owner", "Office", "Lead Tech", "Technician", "Installer", "Apprentice", "Sales"] },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"] },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  payroll: {
    endpoint: "/payroll", title: "Payroll", required: "employee",
    searchKeys: ["employee", "period"],
    columns: [
      { key: "employee", label: "Employee" },
      { key: "period", label: "Period" },
      { key: "hours", label: "Hours" },
      { key: "rate", label: "Rate", render: (r) => (r.rate ? `$${r.rate}` : "") },
      { key: "gross", label: "Gross", render: (r) => (r.gross ? `$${r.gross}` : "") },
    ],
    fields: [
      { key: "employee", label: "Employee" },
      { key: "period", label: "Pay Period (e.g. Jun 1–15)" },
      { key: "hours", label: "Hours Worked" },
      { key: "rate", label: "Hourly Rate ($)" },
      { key: "gross", label: "Gross Pay ($)" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
};

const NAV_GROUPS = [
  { title: "Operations", items: [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "leads", label: "Requests", icon: Inbox },
    { key: "field-service", label: "Field Service", icon: Wrench },
    { key: "my-jobs", label: "My Jobs", icon: Timer },
    { key: "dispatch", label: "Dispatch", icon: Send },
    { key: "pricebook", label: "Pricebook", icon: BookOpen },
    { key: "equipment-hub", label: "Equipment", icon: Wrench },
    { key: "calendar", label: "Calendar", icon: Calendar },
    { key: "customers", label: "Customers", icon: Users },
  ]},
  { title: "New Business", items: [
    { key: "new-business", label: "New Business Engine", icon: TrendingUp },
  ]},
  { title: "Sales & Members", items: [
    { key: "membership", label: "Membership", icon: Crown },
    { key: "comfort-club", label: "Comfort Club", icon: Star },
    { key: "veteran-giveaway", label: "Veteran Giveaway", icon: Award },
    { key: "margins", label: "Margins", icon: TrendingUp },
  ]},
  { title: "Marketing & SEO", items: [
    { key: "blog", label: "Blog & SEO", icon: FileText },
    { key: "content-planner", label: "Content Planner", icon: CalendarDays },
    { key: "ai-visibility", label: "AI Visibility", icon: Sparkles },
    { key: "content-engine", label: "Content Engine", icon: Sparkles },
    { key: "draft-review", label: "Draft Review", icon: FileText },
    { key: "seo-command", label: "SEO Command", icon: Sparkles },
    { key: "topic-ownership", label: "Topic Ownership", icon: Layers },
    { key: "local-gravity", label: "Local Authority", icon: MapPin },
    { key: "competitor-topics", label: "Competitor Topics", icon: Swords },
    { key: "keyword-intel", label: "Keyword Intel", icon: KeyRound },
    { key: "ranking-tracker", label: "Ranking Tracker", icon: LineChart },
    { key: "competitor-intel", label: "Competitor Intel", icon: Swords },
    { key: "ai-search-visibility", label: "AI Search Visibility", icon: Bot },
    { key: "weather-intel", label: "Weather Intel", icon: CloudSun },
    { key: "seo", label: "Daily SEO", icon: Search },
    { key: "marketing-app", label: "Marketing App", icon: Megaphone },
    { key: "promotions", label: "Promotions", icon: Tag },
  ]},
  { title: "Field Tools", items: [
    { key: "forms", label: "Forms", icon: ClipboardList },
    { key: "load-calc", label: "Load Calc", icon: Gauge },
    { key: "labor-calculator", label: "Labor Calculator", icon: Calculator },
    { key: "comfort-app", label: "Comfort App", icon: Smartphone },
    { key: "launch-select", label: "Launch Select", icon: Rocket },
    { key: "selling-comfort", label: "Selling Comfort", icon: Award },
    { key: "warranty", label: "Warranty Compliance", icon: Gauge },
    { key: "guarantees", label: "Guarantees", icon: ShieldCheck },
  ]},
  { title: "Finance & Team", items: [
    { key: "invoices", label: "Invoices", icon: Receipt },
    { key: "expenses", label: "Expenses", icon: Wallet },
    { key: "parts", label: "Parts Catalog", icon: Package },
    { key: "vendors", label: "Vendors", icon: Truck },
    { key: "team", label: "Team", icon: Contact },
    { key: "payroll", label: "Payroll", icon: DollarSign },
  ]},
  { title: "Website & Content", items: [
    { key: "services", label: "Services", icon: Wrench },
    { key: "locations", label: "Service Areas", icon: MapPin },
    { key: "reviews", label: "Reviews", icon: Star },
    { key: "jobs", label: "Careers", icon: Briefcase },
    { key: "company", label: "Company Info", icon: Building2 },
    { key: "redirects", label: "Redirects", icon: GitBranch },
  ]},
];

// ---------- helpers ----------
function JsonField({ label, value, onChange }) {
  const [txt, setTxt] = useState(JSON.stringify(value ?? "", null, 2));
  const [err, setErr] = useState("");
  useEffect(() => { setTxt(JSON.stringify(value ?? "", null, 2)); }, []);
  return (
    <div>
      <Label className="text-navy text-sm">{label}</Label>
      <Textarea
        value={txt}
        onChange={(e) => {
          setTxt(e.target.value);
          try { onChange(JSON.parse(e.target.value)); setErr(""); } catch { setErr("Invalid JSON"); }
        }}
        rows={6}
        className="mt-1 font-mono text-xs border-slate-300"
      />
      {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
    </div>
  );
}

function ImageGenField({ label, value, onChange }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const gen = async () => {
    if (!prompt.trim()) { toast.error("Enter an image description"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/generate-image", { prompt });
      onChange(data.url);
      toast.success("Image generated");
    } catch (e) {
      toast.error("Generation failed: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  return (
    <div>
      <Label className="text-navy text-sm">{label}</Label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 border-slate-300" placeholder="Image URL, or generate with AI below" />
      {value ? <img src={value} alt="preview" className="mt-2 h-28 w-auto rounded border border-slate-200 object-cover" /> : null}
      <div className="mt-2 flex gap-2">
        <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} className="border-slate-300 text-sm" placeholder="Describe the image to generate with AI…" data-testid="imggen-prompt" />
        <button type="button" onClick={gen} disabled={loading} data-testid="imggen-btn" className="inline-flex items-center gap-1 whitespace-nowrap bg-cool text-white px-3 py-2 rounded-md text-sm font-semibold disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate
        </button>
      </div>
    </div>
  );
}

function EntityEditor({ open, onClose, entity, fields, onSave }) {
  const [form, setForm] = useState(entity || {});
  useEffect(() => { setForm(entity || {}); }, [entity, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{form && form._new ? "Add New" : "Edit"}</DialogTitle>
          <DialogDescription className="sr-only">Edit the fields and save your changes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {fields.map((f) => (
            <div key={f.name}>
              {f.type === "textarea" ? (
                <>
                  <Label className="text-navy text-sm">{f.label}</Label>
                  <Textarea value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} rows={f.rows || 4} className="mt-1 border-slate-300" />
                </>
              ) : f.type === "json" ? (
                <JsonField label={f.label} value={form[f.name]} onChange={(v) => set(f.name, v)} />
              ) : f.type === "image" ? (
                <ImageGenField label={f.label} value={form[f.name]} onChange={(v) => set(f.name, v)} />
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={!!form[f.name]} onChange={(e) => set(f.name, e.target.checked)} /> {f.label}</label>
              ) : (
                <>
                  <Label className="text-navy text-sm">{f.label}</Label>
                  <Input value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} className="mt-1 border-slate-300" />
                </>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-slate-600 text-sm">Cancel</button>
          <button onClick={() => onSave(form)} data-testid="entity-save" className="px-4 py-2 rounded-md bg-launch text-white font-semibold text-sm">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EntityManager({ title, endpoint, idKey, columns, fields, blank, listParams }) {
  const qc = useQueryClient();
  const { data: items = [], refetch, isLoading } = useQuery({
    queryKey: ["admin", endpoint],
    queryFn: async () => (await api.get(endpoint, { params: listParams })).data,
  });
  const [editing, setEditing] = useState(null);

  const save = async (form) => {
    try {
      const payload = { ...form }; delete payload._new;
      if (form._new) await api.post(endpoint, payload);
      else await api.put(`${endpoint}/${encodeURIComponent(form[idKey])}`, payload);
      toast.success("Saved");
      setEditing(null); refetch(); qc.invalidateQueries();
    } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || e.message)); }
  };
  const del = async (item) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`${endpoint}/${encodeURIComponent(item[idKey])}`); toast.success("Deleted"); refetch(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold text-navy">{title} <span className="text-slate-400 text-lg font-normal">({items.length})</span></h1>
        <button onClick={() => setEditing({ ...blank, _new: true })} data-testid="entity-add" className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 rounded-md text-sm font-semibold"><Plus className="h-4 w-4" /> Add New</button>
      </div>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left"><tr>{columns.map((c) => <th key={c.key} className="px-4 py-3 font-semibold">{c.label}</th>)}<th className="px-4 py-3"></th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it) => (
                  <tr key={it[idKey]} className="hover:bg-slate-50" data-testid={`row-${it[idKey]}`}>
                    {columns.map((c) => <td key={c.key} className="px-4 py-3 text-slate-700 max-w-xs truncate">{c.render ? c.render(it) : String(it[c.key] ?? "")}</td>)}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setEditing({ ...it })} className="text-slate-400 hover:text-cool p-1" data-testid={`edit-${it[idKey]}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => del(it)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`del-${it[idKey]}`}><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <EntityEditor open={!!editing} onClose={() => setEditing(null)} entity={editing} fields={fields} onSave={save} />
    </div>
  );
}

// ---------- section renderers ----------
function OverviewPanel({ go }) {
  const { data: stats } = useQuery({ queryKey: ["lead-stats"], queryFn: async () => (await api.get("/leads/stats")).data });
  const { data: bookings = [] } = useQuery({ queryKey: ["ov-bookings"], queryFn: async () => (await api.get("/leads")).data });
  const { data: mem } = useQuery({ queryKey: ["ov-mem"], queryFn: async () => (await api.get("/memberships")).data });
  const in30 = Date.now() + 30 * 864e5;
  const renewals = (mem?.customers || [])
    .filter((c) => c.tier !== "standard" && c.renewal_date && new Date(c.renewal_date).getTime() <= in30)
    .sort((a, b) => (a.renewal_date < b.renewal_date ? -1 : 1));
  return (
    <div data-testid="overview-panel">
      <h1 className="text-2xl font-bold text-navy">Command Center</h1>
      <p className="text-slate-500 mb-6">Welcome back — here's how Aquila Air &amp; Heating is doing.</p>
      <Overview stats={stats} bookings={bookings} renewals={renewals} />
    </div>
  );
}

function LeadsPanel() {
  const { data: bookings = [], refetch } = useQuery({ queryKey: ["admin-leads"], queryFn: async () => (await api.get("/leads")).data });
  const { data: team = [] } = useQuery({ queryKey: ["admin-team-req"], queryFn: async () => (await api.get("/team")).data });
  const navigate = useNavigate();
  const [view, setView] = useState("center");
  return (
    <div data-testid="requests-panel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-navy">Leads <span className="text-slate-400 text-lg font-normal">({bookings.length})</span></h1>
        <div className="inline-flex bg-slate-100 rounded-lg p-1">
          <button onClick={() => setView("center")} data-testid="leads-view-center"
            className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${view === "center" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Lead Center</button>
          <button onClick={() => setView("proposals")} data-testid="leads-view-proposals"
            className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${view === "proposals" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Proposal Follow-Up</button>
          <button onClick={() => setView("all")} data-testid="leads-view-all"
            className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${view === "all" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>All Requests</button>
        </div>
      </div>
      {view === "center" && <div className="mb-8"><LeadCenter team={team} onRefresh={refetch} /></div>}
      {view === "proposals" && <div className="mb-8"><ProposalFollowup /></div>}
      {view === "all" && (
        <RequestsTable bookings={bookings} team={team} onRefresh={refetch} onCreateInvoice={async (b) => {
          try {
            await createForm({ customer_name: b.name, form_type: "invoice", title: "Invoice", data: {
              bill_to_name: b.name, bill_to_phone: b.phone || "", bill_to_email: b.email || "", bill_to_address: b.address || "",
              line_items: [{ description: b.service || "HVAC Service", qty: 1, unit_price: b.job_value || "", _uid: `li_${Date.now()}` }], tax_rate: 0,
            } });
            toast.success("Invoice draft created — opening Forms");
            navigate("/admin/forms");
          } catch { toast.error("Could not create invoice"); }
        }} />
      )}
    </div>
  );
}

function CompanyPanel() {
  const { data: company, refetch } = useQuery({ queryKey: ["admin-company"], queryFn: async () => (await api.get("/company")).data });
  const [form, setForm] = useState(null);
  useEffect(() => { if (company) setForm(company); }, [company]);
  if (!form) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, social: { ...(f.social || {}), [k]: v } }));
  const save = async () => { try { await api.put("/company", form); toast.success("Company info saved"); refetch(); } catch { toast.error("Save failed"); } };
  const F = ({ k, label, ta }) => (
    <div>
      <Label className="text-navy text-sm">{label}</Label>
      {ta ? <Textarea value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} rows={3} className="mt-1 border-slate-300" />
        : <Input value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="mt-1 border-slate-300" />}
    </div>
  );
  const SF = ({ k, label }) => (
    <div>
      <Label className="text-navy text-sm">{label}</Label>
      <Input value={form.social?.[k] ?? ""} onChange={(e) => setSocial(k, e.target.value)} placeholder="https://…" data-testid={`company-social-${k}`} className="mt-1 border-slate-300" />
    </div>
  );
  return (
    <div>
      <div className="flex items-center justify-between mb-5"><h1 className="text-2xl font-bold text-navy">Company Info</h1><button onClick={save} data-testid="company-save" className="bg-launch text-white px-4 py-2 rounded-md text-sm font-semibold">Save Changes</button></div>
      <div className="bg-white border border-slate-200 rounded-lg p-6 grid sm:grid-cols-2 gap-4">
        <F k="name" label="Company Name" /><F k="tagline" label="Tagline" />
        <F k="phone" label="Phone (display)" /><F k="phone_href" label="Phone (tel: link, e.g. +13212921189)" />
        <F k="email" label="Email" /><F k="address" label="Address" />
        <F k="primary_service_area" label="Primary Service Area" /><F k="hours" label="Business Hours" />
        <F k="years_experience" label="Years Experience" /><F k="license" label="License / Insured Text" />
        <div className="sm:col-span-2 border-t border-slate-100 pt-4 mt-2"><h3 className="font-semibold text-navy mb-1">Analytics & Search</h3></div>
        <F k="ga4_measurement_id" label="GA4 Measurement ID (e.g. G-XXXXXXX)" /><F k="gsc_verification" label="Search Console Verification Code" />
        <div className="sm:col-span-2"><F k="about_story" label="About Story" ta /></div>
        <div className="sm:col-span-2 border-t border-slate-100 pt-4 mt-2"><h3 className="font-semibold text-navy mb-1">Social Links</h3><p className="text-xs text-slate-500">Shown as icons in the footer and included in your site's structured data (sameAs) for SEO.</p></div>
        <SF k="facebook" label="Facebook URL" /><SF k="tiktok" label="TikTok URL" />
        <SF k="instagram" label="Instagram URL" /><SF k="youtube" label="YouTube URL" />
        <SF k="linkedin" label="LinkedIn URL" />
      </div>
      <p className="mt-3 text-xs text-slate-500">The why-choose-us list can be edited via the API. GA4 ID takes effect on next page load.</p>
    </div>
  );
}

function SeoAuditPanel() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-seo"], queryFn: async () => (await api.get("/seo/audit")).data });
  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  return (
    <div>
      <h1 className="text-2xl font-bold text-navy mb-1">SEO Health Check</h1>
      <p className="text-slate-500 mb-5">{data?.count === 0 ? "No issues found — all pages look healthy!" : `${data?.count} issue(s) found.`}</p>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="px-4 py-3">Page</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Issue</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.issues || []).map((iss, i) => (
              <tr key={i} data-testid={`seo-issue-${i}`}><td className="px-4 py-3 text-navy">{iss.page}</td><td className="px-4 py-3"><span className="bg-slate-100 rounded px-2 py-0.5 text-xs">{iss.type}</span></td><td className="px-4 py-3 text-red-600">{iss.issue}</td></tr>
            ))}
            {data?.count === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-cool">All clear! 🚀</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { section = "overview" } = useParams();
  const [mobileNav, setMobileNav] = useState(false);
  const [roleKey, setRoleKey] = useState(() => localStorage.getItem("launch_active_role") || DEFAULT_ROLE);
  const ROLE_MAP = { admin: "owner", service_tech: "technician" };
  const isStaffUser = !!(user && user.role && !["owner", "admin"].includes(user.role));
  const effectiveRoleKey = isStaffUser ? (ROLE_MAP[user.role] || user.role) : roleKey;
  const activeRole = roleFor(effectiveRoleKey);
  const navGroups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((it) => canSee(activeRole, it.key)) }))
    .filter((g) => g.items.length);
  const changeRole = (k) => { setRoleKey(k); localStorage.setItem("launch_active_role", k); navigate("/admin/overview"); };

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);
  if (user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;
  if (user === false) return null;

  const panels = {
    overview: <RoleDashboard roleKey={effectiveRoleKey} go={(s) => navigate(`/admin/${s}`)} />,
    "content-engine": <BlogEngine />,
    "draft-review": <DraftReview />,
    "seo-command": <SeoCommand />,
    "keyword-intel": <KeywordIntelligence />,
    "ranking-tracker": <ConnectReadyDashboard icon={LineChart} testid="ranking-tracker"
      title="Local Visibility / Ranking Tracker"
      subtitle="Track where Aquila Air & Heating ranks for its most valuable local keywords across Brevard County — map pack, organic positions, and movement over time."
      providers={[
        { name: "Google Search Console", hint: "Free. Real query, impression, click and position data for your own site (OAuth or CSV upload)." },
        { name: "Google Business Profile", hint: "Local map-pack visibility, calls, direction requests and profile views." },
        { name: "SerpApi", hint: "Paid API for tracked keyword ranking positions and local grid results." },
      ]}
      metrics={[
        { label: "Tracked Keywords", desc: "Keywords you're monitoring" },
        { label: "Avg. Position", desc: "Average organic rank" },
        { label: "Map Pack Rank", desc: "Local 3-pack visibility" },
        { label: "Top 3 Keywords", desc: "Keywords ranking #1–3" },
        { label: "Page-One Keywords", desc: "Keywords ranking #1–10" },
        { label: "Impressions (28d)", desc: "Search appearances" },
      ]}
      note="No fabricated data is shown. Once you authorize a provider, this dashboard will populate with your real ranking data and week-over-week movement." />,
    "competitor-intel": <ConnectReadyDashboard icon={Swords} testid="competitor-intel"
      title="Competitor Intelligence"
      subtitle="See which HVAC competitors show up alongside Aquila in Brevard County search results, the keywords they win, and where the biggest gaps and opportunities are."
      providers={[
        { name: "SerpApi", hint: "Discover competitors ranking for your target local keywords." },
        { name: "Ahrefs / SEMrush", hint: "Competitor keyword profiles, backlinks and content gaps (API key required)." },
        { name: "Google Business Profile", hint: "Compare local profile reviews, categories and photos." },
      ]}
      metrics={[
        { label: "Competitors Tracked", desc: "Local rivals monitored" },
        { label: "Shared Keywords", desc: "Terms you both target" },
        { label: "Keyword Gaps", desc: "Terms they win, you don't" },
        { label: "Share of Voice", desc: "Your visibility vs. rivals" },
        { label: "Review Gap", desc: "Their reviews vs. yours" },
        { label: "Content Gaps", desc: "Topics they cover, you don't" },
      ]}
      note="No competitor data is fabricated. Connect a provider to reveal real competitor rankings, keyword gaps and share-of-voice for the Space Coast." />,
    "ai-search-visibility": <ConnectReadyDashboard icon={Bot} testid="ai-search-visibility"
      title="AI Search Visibility Engine"
      subtitle="Monitor whether AI answer engines — ChatGPT, Gemini, Perplexity and Google's AI Overviews — recommend and cite Aquila Air & Heating when locals ask about HVAC on the Space Coast."
      providers={[
        { name: "AI Prompt Monitor", hint: "Automated tracking of brand mentions & citations across major AI assistants (API required)." },
        { name: "Google Search Console", hint: "Approximate AI Overview impressions as they roll out to reporting." },
        { name: "Manual Prompt Log", hint: "Log test prompts and whether Aquila was cited (available once connected)." },
      ]}
      metrics={[
        { label: "Prompts Tracked", desc: "AI questions monitored" },
        { label: "Brand Mentions", desc: "Times Aquila is named" },
        { label: "Citation Rate", desc: "Answers that cite you" },
        { label: "Share of Answers", desc: "Your presence vs. rivals" },
        { label: "AI Overview Wins", desc: "Google AIO appearances" },
        { label: "Cited Pages", desc: "Your pages AI links to" },
      ]}
      note="Your site already ships full JSON-LD entity schema and FAQ-rich content so AI engines can read who you are. This dashboard stays empty until an AI-visibility provider is connected — no numbers are invented." />,
    "new-business": <ProspectingEngine />,
    leads: <LeadsPanel />,
    customers: <Customers />,
    "field-service": <FieldService />,
    dispatch: <DispatchBoard />,
    pricebook: <Pricebook />,
    "my-jobs": <TechJobs />,
    "equipment-hub": <EquipmentHub />,
    calendar: <CalendarBoard />,
    membership: <MembershipManager />,
    "comfort-club": <ResourceCrud config={RESOURCE_CONFIGS["comfort-club"]} />,
    "veteran-giveaway": <GiveawayAdmin />,
    margins: <MarginsCalc />,
    forms: <FormsManager />,
    "content-planner": <ResourceCrud config={RESOURCE_CONFIGS["content-planner"]} />,
    "load-calc": <LoadCalc />,
    "labor-calculator": <LaborCalc />,
    "weather-intel": <WeatherIntel />,
    team: <ResourceCrud config={RESOURCE_CONFIGS.team} />,
    payroll: <ResourceCrud config={RESOURCE_CONFIGS.payroll} />,
    "ai-visibility": <InfoPanel icon={Sparkles} title="AI Visibility" subtitle="Make sure AI assistants (ChatGPT, Gemini, Perplexity) and Google's AI Overviews recommend Aquila Air & Heating when locals ask about HVAC on the Space Coast."
      points={[
        "Your site ships full JSON-LD schema (HVACBusiness, Service, FAQ, Reviews) so AI engines can read exactly who you are and what you do.",
        "FAQ-rich service and location pages are the content AI answers pull from — keep them detailed and current.",
        "Consistent NAP (name, address, phone) across the site strengthens entity recognition.",
        "Publish helpful, question-based blog posts to become the cited source for HVAC answers in your area.",
      ]}
      actions={[{ label: "Run the SEO Health Check", to: "/admin/seo" }, { label: "Plan new content", to: "/admin/content-planner" }, { label: "Edit Blog & SEO", to: "/admin/blog" }]} />,
    "comfort-app": <InfoPanel icon={Smartphone} title="Comfort App" subtitle="A homeowner-facing companion for Comfort Club members — reminders, service history, and one-tap booking. This is your control hub to manage what members see."
      points={[
        "Members get maintenance reminders timed to Florida's cooling season.",
        "One-tap request booking flows straight into your Requests inbox.",
        "Service history and membership status stay in sync with the Comfort Club roster.",
        "Push seasonal promos and tune-up offers to keep systems healthy.",
      ]}
      actions={[{ label: "Manage Comfort Club members", to: "/admin/comfort-club" }, { label: "Edit membership plans", to: "/admin/membership" }, { label: "View public site", to: "/maintenance-plan" }]} />,
    "marketing-app": <InfoPanel icon={Megaphone} title="Marketing App" subtitle="Everything that fills your pipeline — promotions, lead forms, reviews, and content — in one launchpad."
      points={[
        "Spin up seasonal offers and promo codes that appear on your Specials page.",
        "Build custom intake and quote forms with shareable links for ads and social.",
        "Collect and showcase Google reviews to build trust and local ranking.",
        "Plan and publish content that drives organic traffic.",
      ]}
      actions={[{ label: "Manage Promotions", to: "/admin/promotions" }, { label: "Build a Form", to: "/admin/forms" }, { label: "Manage Reviews", to: "/admin/reviews" }, { label: "Content Planner", to: "/admin/content-planner" }]} />,
    company: <CompanyPanel />,
    "launch-select": <LaunchSelect />,
    "topic-ownership": <TopicOwnership />,
    "local-gravity": <LocalGravity />,
    "competitor-topics": <CompetitorTopics />,
    "selling-comfort": <SellingComfort />,
    warranty: <WarrantyCompliance />,
    guarantees: (
      <div>
        <div className="mb-5">
          <h2 className="text-xl font-bold text-navy">Our Guarantees</h2>
          <p className="text-sm text-slate-500">The written promises we make to every customer. These appear on the public website (<span className="font-medium">/guarantees</span>) and back your sales presentations.</p>
        </div>
        <GuaranteesGrid />
      </div>
    ),
    seo: <SeoAuditPanel />,
    invoices: <ResourceCrud config={RESOURCE_CONFIGS.invoices} />,
    parts: <PartsCatalog />,
    expenses: <ResourceCrud config={RESOURCE_CONFIGS.expenses} />,
    vendors: <ResourceCrud config={RESOURCE_CONFIGS.vendors} />,
    services: <EntityManager title="Services" endpoint="/services" idKey="slug" listParams={{ all: true }}
      columns={[{ key: "name", label: "Name" }, { key: "category", label: "Category" }, { key: "slug", label: "URL", render: (i) => `/${i.slug}` }, { key: "published", label: "Live", render: (i) => (i.published ? "Yes" : "No") }]}
      blank={{ slug: "", name: "", category: "Air Conditioning", published: true, seo_title: "", meta_description: "", h1: "", intro: "", explanation: "", signs: [], benefits: [], process: [], faqs: [], related_services: [], image: "", image_alt: "" }}
      fields={[{ name: "slug", label: "URL Slug (e.g. ac-repair)" }, { name: "name", label: "Name" }, { name: "category", label: "Category" }, { name: "seo_title", label: "SEO Title" }, { name: "meta_description", label: "Meta Description", type: "textarea", rows: 2 }, { name: "h1", label: "H1 Heading" }, { name: "intro", label: "Intro", type: "textarea" }, { name: "explanation", label: "Explanation", type: "textarea", rows: 5 }, { name: "signs", label: "Signs (JSON array)", type: "json" }, { name: "benefits", label: "Benefits (JSON array)", type: "json" }, { name: "process", label: "Process (JSON array)", type: "json" }, { name: "faqs", label: "FAQs (JSON [{q,a}])", type: "json" }, { name: "related_services", label: "Related Services (JSON slugs)", type: "json" }, { name: "image", label: "Image URL", type: "image" }, { name: "image_alt", label: "Image Alt Text" }, { name: "published", label: "Published", type: "checkbox" }]} />,
    locations: <EntityManager title="Service Areas" endpoint="/locations" idKey="slug"
      columns={[{ key: "city", label: "City" }, { key: "county", label: "County" }, { key: "slug", label: "Slug" }, { key: "published", label: "Live", render: (i) => (i.published ? "Yes" : "No") }]}
      blank={{ slug: "", city: "", county: "Brevard County", state: "FL", published: true, seo_title: "", meta_description: "", intro: "", climate: "", neighborhoods: [], common_problems: [], faqs: [], image: "", image_alt: "" }}
      fields={[{ name: "slug", label: "URL Slug (e.g. cocoa-fl)" }, { name: "city", label: "City" }, { name: "county", label: "County" }, { name: "seo_title", label: "SEO Title" }, { name: "meta_description", label: "Meta Description", type: "textarea", rows: 2 }, { name: "intro", label: "Intro", type: "textarea" }, { name: "climate", label: "Climate/Home Notes", type: "textarea" }, { name: "neighborhoods", label: "Neighborhoods (JSON array)", type: "json" }, { name: "common_problems", label: "Common Problems (JSON array)", type: "json" }, { name: "faqs", label: "FAQs (JSON [{q,a}])", type: "json" }, { name: "image", label: "Image URL", type: "image" }, { name: "image_alt", label: "Image Alt" }, { name: "published", label: "Published", type: "checkbox" }]} />,
    blog: <EntityManager title="Blog Posts" endpoint="/blog" idKey="slug"
      columns={[{ key: "title", label: "Title" }, { key: "category", label: "Category" }, { key: "status", label: "Status" }]}
      blank={{ slug: "", title: "", category: "HVAC Tips", author: "Aquila Air & Heating", excerpt: "", read_time: "4 min", content: "", seo_title: "", meta_description: "", featured_image: "", image_alt: "", related_services: [], faqs: [], status: "published" }}
      fields={[{ name: "slug", label: "URL Slug" }, { name: "title", label: "Title" }, { name: "category", label: "Category" }, { name: "author", label: "Author" }, { name: "excerpt", label: "Excerpt", type: "textarea", rows: 2 }, { name: "read_time", label: "Read Time" }, { name: "content", label: "Content (Markdown)", type: "textarea", rows: 10 }, { name: "seo_title", label: "SEO Title" }, { name: "meta_description", label: "Meta Description", type: "textarea", rows: 2 }, { name: "featured_image", label: "Featured Image URL", type: "image" }, { name: "image_alt", label: "Image Alt" }, { name: "related_services", label: "Related Services (JSON slugs)", type: "json" }, { name: "faqs", label: "FAQs (JSON [{q,a}])", type: "json" }, { name: "status", label: "Status (published/draft)" }]} />,
    reviews: <EntityManager title="Reviews" endpoint="/reviews" idKey="id"
      columns={[{ key: "name", label: "Name" }, { key: "rating", label: "Rating" }, { key: "location", label: "Location" }, { key: "source", label: "Source" }]}
      blank={{ name: "", text: "", rating: 5, source: "Google", location: "", date: "", published: true }}
      fields={[{ name: "name", label: "Customer Name" }, { name: "text", label: "Review Text", type: "textarea" }, { name: "rating", label: "Rating (1-5)" }, { name: "source", label: "Source" }, { name: "location", label: "Location" }, { name: "date", label: "Date" }, { name: "published", label: "Published", type: "checkbox" }]} />,
    promotions: <EntityManager title="Promotions" endpoint="/promotions" idKey="id" listParams={{ active_only: false }}
      columns={[{ key: "title", label: "Title" }, { key: "code", label: "Code" }, { key: "expires_at", label: "Expires" }, { key: "published", label: "Live", render: (i) => (i.published ? "Yes" : "No") }]}
      blank={{ title: "", description: "", code: "", expires_at: "", published: true }}
      fields={[{ name: "title", label: "Title" }, { name: "description", label: "Description", type: "textarea" }, { name: "code", label: "Promo Code" }, { name: "expires_at", label: "Expires (YYYY-MM-DD)" }, { name: "published", label: "Published", type: "checkbox" }]} />,
    jobs: <EntityManager title="Job Postings" endpoint="/jobs" idKey="id"
      columns={[{ key: "title", label: "Title" }, { key: "location", label: "Location" }, { key: "employment_type", label: "Type" }]}
      blank={{ title: "", location: "Space Coast, FL", employment_type: "Full-time", description: "", requirements: "", benefits: "", published: true }}
      fields={[{ name: "title", label: "Job Title" }, { name: "location", label: "Location" }, { name: "employment_type", label: "Employment Type" }, { name: "description", label: "Description", type: "textarea", rows: 5 }, { name: "requirements", label: "Requirements", type: "textarea" }, { name: "benefits", label: "Benefits", type: "textarea" }, { name: "published", label: "Published", type: "checkbox" }]} />,
    redirects: <EntityManager title="Redirects" endpoint="/redirects" idKey="id"
      columns={[{ key: "from_path", label: "From" }, { key: "to_path", label: "To" }, { key: "type", label: "Type" }]}
      blank={{ from_path: "", to_path: "", type: "301" }}
      fields={[{ name: "from_path", label: "From Path (e.g. /old-page)" }, { name: "to_path", label: "To Path (e.g. /ac-repair)" }, { name: "type", label: "Type (301/302)" }]} />,
  };

  return (
    <div className="min-h-screen flex bg-slate-100">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-navy text-white flex flex-col transition-transform ${mobileNav ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="flex items-center gap-2 px-5 h-16 border-b border-white/10">
          <Rocket className="h-6 w-6 text-launch" /><span className="font-heading font-extrabold">LAUNCH Command</span>
        </div>
        <div className="px-4 py-3 border-b border-white/10">
          {isStaffUser ? (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Signed in as</label>
              <p className="mt-1 text-sm font-semibold text-white" data-testid="staff-identity">{user.name} · {user.title || activeRole.label}</p>
            </div>
          ) : (
            <>
              {user.title && <p className="mb-2 text-sm font-semibold text-white" data-testid="user-title">{user.name} · {user.title}</p>}
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Viewing as</label>
              <select value={roleKey} onChange={(e) => changeRole(e.target.value)} data-testid="role-switcher"
                className="mt-1 w-full bg-white/10 border border-white/15 text-white text-sm rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-launch/40">
                {ROLES.map((r) => <option key={r.key} value={r.key} className="text-slate-900">{r.label}</option>)}
              </select>
            </>
          )}
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {navGroups.map((grp) => (
            <div key={grp.title} className="mb-2">
              <p className="px-5 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{grp.title}</p>
              {grp.items.map((s) => {
                const Icon = s.icon;
                const active = section === s.key;
                return (
                  <button key={s.key} onClick={() => { navigate(`/admin/${s.key}`); setMobileNav(false); }} data-testid={`admin-nav-${s.key}`}
                    className={`w-full flex items-center gap-3 px-5 py-2 text-sm font-medium transition-colors ${active ? "bg-white/10 text-white border-l-2 border-launch" : "text-slate-300 hover:bg-white/5"}`}>
                    <Icon className="h-4 w-4" /> {s.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <Link to="/" target="_blank" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"><ExternalLink className="h-4 w-4" /> View Site</Link>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"><LogOut className="h-4 w-4" /> Log Out</button>
        </div>
      </aside>
      {mobileNav && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileNav(false)} />}

      <div className="flex-1 min-w-0">
        <header className="lg:hidden flex items-center gap-3 bg-white border-b border-slate-200 px-4 h-14">
          <button onClick={() => setMobileNav(true)} data-testid="admin-mobile-toggle"><Menu className="h-6 w-6 text-navy" /></button>
          <span className="font-heading font-bold text-navy">Dashboard</span>
        </header>
        <main className="p-5 sm:p-8">{panels[section] || panels.overview}</main>
      </div>
    </div>
  );
}
